import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import {
  initialSyntheticWellLog,
  syntheticWellLogSchema,
} from '~/utils/modules/supporting-object';

const CREATE_SYNTHETIC_WELL_LOG = graphql(`
  mutation CreateSyntheticWellLog($input: SyntheticWellLogCreateInput!) {
    syntheticWellLogCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

type Props = SOParent & {
  refetchQueries?: RefetchQueries;
};

export function SyntheticWellLogCreateForm({
  outcropId,
  studyId,
  refetchQueries,
}: Props) {
  const [createSyntheticWellLog, { loading, error }] = useMutation(
    CREATE_SYNTHETIC_WELL_LOG,
    { refetchQueries },
  );

  const form = useZodForm({
    schema: syntheticWellLogSchema,
    values: initialSyntheticWellLog(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createSyntheticWellLog({
        variables: {
          input: {
            ...values,
            outcropId,
            studyId,
          },
        },
      });
      toast.success('Synthetic well log created successfully.');
      form.reset();
    } catch (err) {
      console.log('Error creating synthetic well log', err);
      toast.error('There was a problem creating the synthetic well log.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="SYNTHETIC_WELL_LOG" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
